import type { AnnotationSide } from "@pierre/diffs"
import { ACTION_COLORS, ACTION_ICONS, type SavedActionColor, type SavedActionIcon } from "../actions/actionTypes"
import { isProjectColor, parseProjectChars, normalizeProjectEmoji, normalizeProjectGlyph, type ProjectColor } from "../projectAppearance"
import type { Comment, CommentSelectedText, CommentSource, CommentSubmission, User } from "../types"
import type { PersonalSettings } from "./personalSettingsStore"

export const REPOS_NAMESPACE = "wildlark.repos"
export const TASKS_NAMESPACE = "wildlark.tasks"
export const SETTINGS_NAMESPACE = "wildlark.settings"
export const SETTINGS_KEY = "personal.v1"

export function repoRecordKey(repoId: string): string {
    return `repo.${repoId}`
}

export function taskRecordKey(taskId: string): string {
    return `task.${taskId}`
}

export function repoIdFromRecordKey(key: string): string | null {
    return key.startsWith("repo.") ? key.slice("repo.".length) || null : null
}

export function taskIdFromRecordKey(key: string): string | null {
    return key.startsWith("task.") ? key.slice("task.".length) || null : null
}

export type ProviderId = "codex" | "claude" | (string & {})
export type TaskHistorySource = "provider-native" | "legacy-wildlark" | "mixed"
export type TaskSessionRole = "primary" | "auxiliary"

export interface TaskSessionRef extends Record<string, unknown> {
    role: TaskSessionRole
    providerId: ProviderId
    predecessorSessionId?: string
    parentSessionId?: string
    nestSessionId?: string
    providerSessionId?: string
    cwd?: string
    executionDirectory?: string
    mode?: string
    model?: string
    thinking?: ThinkingLevel
    label?: string
    startRequestId?: string
    startTurnId?: string
    lastTurnId?: string
    lastTurnClientRequestId?: string
    createdAt?: string
    updatedAt?: string
}

export interface TaskWorktreeRecovery extends Record<string, unknown> {
    backupCommit: string
    backupMessage: string
    transferredAt: string
    requested: number
    succeeded: number
    failed: number
}

export interface TaskAgentGitContext extends Record<string, unknown> {
    v: 1
    baseDir: string
    worktreePath: string
    startHead?: string
    deviceId: string
    createdAt: string
}

export interface TaskExternalLink extends Record<string, unknown> {
    id: string
    provider: "github" | "gitlab"
    kind: "pull_request" | "merge_request"
    host: string
    owner: string
    repository: string
    number: number
    url: string
    providerNodeId?: string
    watch: "active" | "unsupported"
    linkedAt: string
}

export interface TaskAttention {
    at: string
    source: "user" | "pr"
    sleepUntil?: string
    reason?: string
}

export function parseTaskAttention(value: unknown): TaskAttention | undefined {
    if (!isRecord(value) || typeof value.at !== "string" || !Number.isFinite(Date.parse(value.at)) || (value.source !== "user" && value.source !== "pr"))
        return undefined
    const sleepUntil = value.sleepUntil
    if (sleepUntil !== undefined && (typeof sleepUntil !== "string" || (sleepUntil !== "indefinite" && !Number.isFinite(Date.parse(sleepUntil)))))
        return undefined
    return {
        at: new Date(value.at).toISOString(),
        source: value.source,
        ...(typeof sleepUntil === "string" ? { sleepUntil: sleepUntil === "indefinite" ? sleepUntil : new Date(sleepUntil).toISOString() } : {}),
        ...(typeof value.reason === "string" ? { reason: value.reason.slice(0, 1000) } : {}),
    }
}

export type TaskPullRequestAutomationPhase = "watching" | "queued" | "running" | "attention"
export type TaskPullRequestOperation = "repair" | "landing" | "closeout"

/** Backend-owned summary of the task's PR automation record. Derived, never authoritative. */
export interface TaskPullRequestAutomation extends Record<string, unknown> {
    v: 1
    linkId: string
    since: string
    phase: TaskPullRequestAutomationPhase
    delivery?: { id: string; kind: string; operation: TaskPullRequestOperation }
    hold?: { reason: string; source: "snapshot" | "delivery" | "migration"; detail?: string; since: string }
    updatedAt: string
}

export type TaskDirectorySelection = { mode: "manual"; path: string }

export interface TaskActionRunAction extends Record<string, unknown> {
    id: string
    name: string
    icon: SavedActionIcon
    color: SavedActionColor
}

export interface TaskActionRun extends Record<string, unknown> {
    clientRequestId: string
    sessionId: string
    turnId: string
    inputFingerprint: string
    actions: TaskActionRunAction[]
    createdAt: string
}

export const MAX_TASK_ACTION_RUNS = 64

export type TaskQueuePriority = "major"

export interface RepoRecord extends Record<string, unknown> {
    v: 1
    id: string
    name: string
    path: string
    workspaceId: string
    archived?: boolean
    color?: ProjectColor
    glyph?: string
    projectChars?: string
    projectEmoji?: string
    createdAt: string
    updatedAt: string
    legacyRepoId?: string
}

export interface TaskHandoff {
    thinking?: ThinkingLevel
    clientRequestId: string
    sessionId: string
    harnessId: string
    model: string
}

export type { AgentTarget as TaskDelegateTarget } from "../agentTarget"
import { isThinkingLevel, type AgentTarget as TaskDelegateTarget, type ThinkingLevel } from "../agentTarget"

export interface TaskRecord extends Record<string, unknown> {
    v: 1
    id: string
    repoId: string
    workspaceId: string
    title: string
    titleManuallySet?: boolean
    titleAgentInitialized?: boolean
    globalSystemPrompt?: string
    activeSessionId?: string
    handoff?: TaskHandoff
    delegateTarget?: TaskDelegateTarget | null
    sessions: TaskSessionRef[]
    closed?: boolean
    closedAt?: string
    deletingAt?: string
    pinned?: boolean
    queuePriority?: TaskQueuePriority
    lastViewedAt?: string
    createdAt: string
    updatedAt: string
    legacyTaskId?: string
    historySource?: TaskHistorySource
    enabledMcpServerIds?: string[]
    comments?: Comment[]
    actionRuns?: TaskActionRun[]
    /** Exact non-project roots owned by this task. */
    additionalDirs?: string[]
    agentGitContext?: TaskAgentGitContext
    directorySelection?: TaskDirectorySelection
    externalLinks?: TaskExternalLink[]
    pullRequestAutomation?: TaskPullRequestAutomation
    attention?: TaskAttention
    /** Legacy pre-reducer wait projection. The backend removes it; writes must never resurrect it. */
    pullRequestWait?: unknown
    /** Legacy read-only worktree metadata. New task records do not write this field. */
    deviceEnvironments?: unknown[]
    pullRequest?: Record<string, unknown>
    /** Legacy read-only recovery metadata. New task records do not write this field. */
    worktreeRecovery?: TaskWorktreeRecovery
}

export interface PersonalSettingsRecord extends Record<string, unknown> {
    v: 1
    settings: PersonalSettings
    updatedAt: string
}

const TASK_HISTORY_SOURCES = new Set<TaskHistorySource>(["provider-native", "legacy-wildlark", "mixed"])
const REPO_DERIVED_FIELDS = new Set(["tasks", "gitInfo", "color", "glyph", "projectChars", "projectEmoji"])
const TASK_DERIVED_FIELDS = new Set([
    "activeSessionId",
    "handoff",
    "slug",
    "status",
    "activityAt",
    "lastEvent",
    "lastEventAt",
    "usage",
    "order",
    "index",
    "preview",
    "agentGitContext",
    "directorySelection",
    "titleManuallySet",
    "titleAgentInitialized",
    "globalSystemPrompt",
    "delegateTarget",
    "actionRuns",
    "additionalDirs",
    "cancelledPlanEventId",
    "flows",
])
const TASK_RETIRED_FIELDS_PRESERVED_ON_WRITE = ["cancelledPlanEventId", "flows"]

export interface TaskRecordWriteSource {
    /** Minimal source-only state needed to avoid erasing retired data during unrelated writes. */
    fields: Readonly<Record<string, unknown>>
    sessionRoles: ReadonlyMap<string, string>
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value)
}

function stringField(record: Record<string, unknown>, key: string): string | null {
    const value = record[key]
    return typeof value === "string" && value ? value : null
}

function optionalStringField(record: Record<string, unknown>, key: string): string | undefined {
    const value = record[key]
    return typeof value === "string" ? value : undefined
}

function optionalBooleanField(record: Record<string, unknown>, key: string): boolean | undefined {
    const value = record[key]
    return typeof value === "boolean" ? value : undefined
}

function optionalStringArrayField(record: Record<string, unknown>, key: string): string[] | undefined {
    const value = record[key]
    return Array.isArray(value) && value.every((item) => typeof item === "string") ? value : undefined
}

function parseTaskActionRunAction(value: unknown): TaskActionRunAction | null {
    if (!isRecord(value)) return null
    const id = stringField(value, "id")
    const name = stringField(value, "name")
    const icon = ACTION_ICONS.find((candidate) => candidate === value.icon)
    const color = ACTION_COLORS.find((candidate) => candidate === value.color)
    if (!id || id.length > 120 || !name || name.length > 120 || !icon || !color) return null
    return { ...value, id, name, icon, color }
}

function parseTaskActionRun(value: unknown): TaskActionRun | null {
    if (!isRecord(value) || !Array.isArray(value.actions) || value.actions.length === 0 || value.actions.length > 8) return null
    const clientRequestId = stringField(value, "clientRequestId")
    const sessionId = stringField(value, "sessionId")
    const turnId = stringField(value, "turnId")
    const inputFingerprint = stringField(value, "inputFingerprint")
    const createdAt = stringField(value, "createdAt")
    const actions = value.actions.map(parseTaskActionRunAction)
    if (
        !clientRequestId ||
        clientRequestId.length > 4096 ||
        !sessionId ||
        sessionId.length > 256 ||
        !turnId ||
        turnId.length > 256 ||
        !inputFingerprint ||
        inputFingerprint.length > 120 ||
        !createdAt ||
        actions.some((action) => action === null)
    ) {
        return null
    }
    return {
        ...value,
        clientRequestId,
        sessionId,
        turnId,
        inputFingerprint,
        actions: actions.filter((action): action is TaskActionRunAction => action !== null),
        createdAt,
    }
}

function parseTaskActionRuns(value: unknown): TaskActionRun[] | undefined {
    if (!Array.isArray(value)) return undefined
    return value
        .slice(-MAX_TASK_ACTION_RUNS)
        .map(parseTaskActionRun)
        .filter((run): run is TaskActionRun => run !== null)
}

function parseTaskExternalLink(value: unknown): TaskExternalLink | null {
    if (!isRecord(value)) return null
    const id = stringField(value, "id")
    const host = stringField(value, "host")
    const owner = stringField(value, "owner")
    const repository = stringField(value, "repository")
    const url = stringField(value, "url")
    const linkedAt = stringField(value, "linkedAt")
    const number = numberField(value, "number")
    const providerNodeId = optionalStringField(value, "providerNodeId")
    const provider = value.provider === "github" || value.provider === "gitlab" ? value.provider : null
    const kind = value.kind === "pull_request" || value.kind === "merge_request" ? value.kind : null
    const watch = value.watch === "active" || value.watch === "unsupported" ? value.watch : null
    const validProviderShape =
        (provider === "github" && kind === "pull_request" && watch === "active") ||
        (provider === "gitlab" && kind === "merge_request" && watch === "unsupported")
    const expectedURL =
        provider === "github"
            ? `https://github.com/${owner}/${repository}/pull/${number}`
            : provider === "gitlab"
              ? `https://gitlab.com/${owner}/${repository}/-/merge_requests/${number}`
              : null
    const expectedId =
        provider === "github"
            ? `github:github.com/${owner}/${repository}/pull/${number}`.toLowerCase()
            : provider === "gitlab"
              ? `gitlab:gitlab.com/${owner}/${repository}/merge_request/${number}`.toLowerCase()
              : null
    if (
        !id ||
        !validProviderShape ||
        !host ||
        !owner ||
        !repository ||
        !url ||
        !linkedAt ||
        number === null ||
        !Number.isInteger(number) ||
        number <= 0 ||
        !watch ||
        id !== expectedId ||
        url !== expectedURL ||
        host !== (provider === "github" ? "github.com" : "gitlab.com") ||
        Number.isNaN(Date.parse(linkedAt))
    ) {
        return null
    }
    return {
        ...value,
        id,
        provider,
        kind,
        host,
        owner,
        repository,
        number,
        url,
        ...(providerNodeId ? { providerNodeId } : {}),
        watch,
        linkedAt,
    }
}

function parseTaskExternalLinks(value: unknown): TaskExternalLink[] | undefined {
    if (!Array.isArray(value)) return undefined
    return value
        .slice(0, 8)
        .map(parseTaskExternalLink)
        .filter((link): link is TaskExternalLink => link !== null)
}

const TASK_PULL_REQUEST_PHASES = new Set<TaskPullRequestAutomationPhase>(["watching", "queued", "running", "attention"])
const TASK_PULL_REQUEST_OPERATIONS = new Set<TaskPullRequestOperation>(["repair", "landing", "closeout"])

function parseTaskPullRequestAutomation(value: unknown): TaskPullRequestAutomation | undefined {
    if (!isRecord(value) || value.v !== 1) return undefined
    const linkId = stringField(value, "linkId")
    const since = stringField(value, "since")
    const updatedAt = stringField(value, "updatedAt")
    const phase = value.phase
    if (!linkId || !since || !updatedAt || !TASK_PULL_REQUEST_PHASES.has(phase as TaskPullRequestAutomationPhase)) return undefined
    const parsed: TaskPullRequestAutomation = {
        ...value,
        v: 1,
        linkId,
        since,
        updatedAt,
        phase: phase as TaskPullRequestAutomationPhase,
    }
    delete parsed.policy
    const delivery = value.delivery
    if (
        isRecord(delivery) &&
        typeof delivery.id === "string" &&
        typeof delivery.kind === "string" &&
        TASK_PULL_REQUEST_OPERATIONS.has(delivery.operation as TaskPullRequestOperation)
    ) {
        parsed.delivery = { id: delivery.id, kind: delivery.kind, operation: delivery.operation as TaskPullRequestOperation }
    } else delete parsed.delivery
    const hold = value.hold
    if (
        isRecord(hold) &&
        typeof hold.reason === "string" &&
        hold.reason.trim() &&
        (hold.source === "snapshot" || hold.source === "delivery" || hold.source === "migration")
    ) {
        parsed.hold = { reason: hold.reason.trim(), source: hold.source, since: typeof hold.since === "string" ? hold.since : since }
        if (typeof hold.detail === "string" && hold.detail.trim()) parsed.hold.detail = hold.detail.trim().slice(0, 1000)
    } else delete parsed.hold
    return parsed
}

export function parseTaskAgentGitContext(value: unknown): TaskAgentGitContext | undefined {
    if (!isRecord(value) || value.v !== 1) return undefined
    const baseDir = stringField(value, "baseDir")
    const worktreePath = stringField(value, "worktreePath")
    const deviceId = stringField(value, "deviceId")
    const createdAt = stringField(value, "createdAt")
    const startHead = optionalStringField(value, "startHead")
    if (!baseDir || !worktreePath || !deviceId || !createdAt || (value.startHead !== undefined && !startHead)) return undefined
    return {
        ...value,
        v: 1,
        baseDir,
        worktreePath,
        ...(startHead ? { startHead } : {}),
        deviceId,
        createdAt,
    }
}

export function parseTaskDirectorySelection(value: unknown): TaskDirectorySelection | undefined {
    if (!isRecord(value) || value.mode !== "manual") return undefined
    const path = stringField(value, "path")
    return path ? { mode: "manual", path } : undefined
}

function numberField(record: Record<string, unknown>, key: string): number | null {
    const value = record[key]
    return typeof value === "number" && Number.isFinite(value) ? value : null
}

function copyUnknownFields(record: Record<string, unknown>, omit: Set<string>): Record<string, unknown> {
    const copied: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(record)) {
        if (!omit.has(key)) copied[key] = value
    }
    return copied
}

function parseLineRange(value: Record<string, unknown>): { lineStart: number; lineEnd: number } | null {
    const lineStart = numberField(value, "lineStart")
    const lineEnd = numberField(value, "lineEnd")
    return lineStart !== null && lineEnd !== null ? { lineStart, lineEnd } : null
}

function parseAnnotationSide(value: unknown): AnnotationSide | null {
    // Boundary parser: @pierre/diffs owns the concrete side string union, while
    // persisted records are plain JSON from old and new clients.
    return typeof value === "string" ? (value as AnnotationSide) : null
}

function parseCommentSource(value: unknown): CommentSource | null {
    if (!isRecord(value) || typeof value.type !== "string") return null
    const range = parseLineRange(value)
    if (!range) return null

    switch (value.type) {
        case "file": {
            const filePath = stringField(value, "filePath")
            return filePath ? { type: "file", filePath, ...range } : null
        }
        case "diff": {
            const eventId = stringField(value, "eventId")
            const filePath = stringField(value, "filePath")
            const side = parseAnnotationSide(value.side)
            return eventId && filePath && side ? { type: "diff", eventId, filePath, side, ...range } : null
        }
        case "patch": {
            const snapshotEventId = stringField(value, "snapshotEventId")
            const filePath = stringField(value, "filePath")
            const side = parseAnnotationSide(value.side)
            return snapshotEventId && filePath && side ? { type: "patch", snapshotEventId, filePath, side, ...range } : null
        }
        case "llm_output": {
            const eventId = stringField(value, "eventId")
            return eventId ? { type: "llm_output", eventId, ...range } : null
        }
        case "edit_diff": {
            const actionEventId = stringField(value, "actionEventId")
            const toolUseId = stringField(value, "toolUseId")
            const filePath = stringField(value, "filePath")
            const side = parseAnnotationSide(value.side)
            return actionEventId && toolUseId && filePath && side ? { type: "edit_diff", actionEventId, toolUseId, filePath, side, ...range } : null
        }
        case "write_diff": {
            const actionEventId = stringField(value, "actionEventId")
            const toolUseId = stringField(value, "toolUseId")
            const filePath = stringField(value, "filePath")
            return actionEventId && toolUseId && filePath ? { type: "write_diff", actionEventId, toolUseId, filePath, ...range } : null
        }
        case "bash_output": {
            const actionEventId = stringField(value, "actionEventId")
            const toolUseId = stringField(value, "toolUseId")
            return actionEventId && toolUseId ? { type: "bash_output", actionEventId, toolUseId, ...range } : null
        }
        case "assistant_text": {
            const actionEventId = stringField(value, "actionEventId")
            const messageIndex = numberField(value, "messageIndex")
            return actionEventId && messageIndex !== null ? { type: "assistant_text", actionEventId, messageIndex, ...range } : null
        }
        default:
            return null
    }
}

function parseSelectedText(value: unknown): CommentSelectedText | null {
    if (!isRecord(value)) return null
    const text = optionalStringField(value, "text")
    const linesBefore = optionalStringField(value, "linesBefore")
    const linesAfter = optionalStringField(value, "linesAfter")
    return text !== undefined && linesBefore !== undefined && linesAfter !== undefined ? { text, linesBefore, linesAfter } : null
}

function parseCommentSubmission(value: unknown): CommentSubmission | null {
    if (!isRecord(value)) return null
    const clientRequestId = stringField(value, "clientRequestId")
    const sessionId = stringField(value, "sessionId")
    const turnId = stringField(value, "turnId")
    const submittedAt = stringField(value, "submittedAt")
    return clientRequestId && sessionId && turnId && submittedAt ? { clientRequestId, sessionId, turnId, submittedAt } : null
}

function parseUser(value: unknown): User | null {
    if (!isRecord(value)) return null
    const id = stringField(value, "id")
    const email = stringField(value, "email")
    return id && email ? { id, email } : null
}

export function parseComment(value: unknown): Comment | null {
    if (!isRecord(value)) return null
    const id = stringField(value, "id")
    const content = optionalStringField(value, "content")
    const source = parseCommentSource(value.source)
    const selectedText = parseSelectedText(value.selectedText)
    const author = parseUser(value.author)
    const createdAt = stringField(value, "createdAt")
    if (!id || content === undefined || !source || !selectedText || !author || !createdAt) return null
    const submission = value.submission === undefined ? undefined : parseCommentSubmission(value.submission)
    return {
        id,
        content,
        source,
        selectedText,
        author,
        createdAt,
        ...(typeof value.updatedAt === "string" ? { updatedAt: value.updatedAt } : {}),
        ...(submission ? { submission } : {}),
    }
}

function commentsField(value: unknown): Comment[] | null {
    if (!Array.isArray(value)) return null
    const comments = value.map(parseComment)
    return comments.some((comment) => comment === null) ? null : comments.filter((comment): comment is Comment => comment !== null)
}

function withOptionalString<T extends Record<string, unknown>>(record: T, key: keyof T & string, value: string | undefined): T {
    if (value === undefined) {
        delete record[key]
    } else {
        record[key] = value as T[keyof T & string]
    }
    return record
}

function withOptionalBoolean<T extends Record<string, unknown>>(record: T, key: keyof T & string, value: boolean | undefined): T {
    if (value === undefined) {
        delete record[key]
    } else {
        record[key] = value as T[keyof T & string]
    }
    return record
}

export function parseTaskSessionRef(value: unknown): TaskSessionRef | null {
    if (!isRecord(value)) return null
    const storedRole = value.role
    const providerId = stringField(value, "providerId")
    const nestSessionId = stringField(value, "nestSessionId")
    const providerSessionId = optionalStringField(value, "providerSessionId")
    if (typeof storedRole !== "string" || !providerId || (!nestSessionId && !providerSessionId)) {
        return null
    }
    const role: TaskSessionRole = storedRole === "primary" ? "primary" : "auxiliary"

    const parsed: TaskSessionRef = {
        ...value,
        role,
        providerId,
    }
    withOptionalString(parsed, "nestSessionId", nestSessionId ?? undefined)
    withOptionalString(parsed, "providerSessionId", providerSessionId)
    withOptionalString(parsed, "parentSessionId", optionalStringField(value, "parentSessionId"))
    withOptionalString(parsed, "predecessorSessionId", optionalStringField(value, "predecessorSessionId"))
    withOptionalString(parsed, "cwd", optionalStringField(value, "cwd"))
    withOptionalString(parsed, "executionDirectory", optionalStringField(value, "executionDirectory"))
    withOptionalString(parsed, "mode", optionalStringField(value, "mode"))
    withOptionalString(parsed, "model", optionalStringField(value, "model"))
    if (isThinkingLevel(value.thinking)) parsed.thinking = value.thinking
    else delete parsed.thinking
    withOptionalString(parsed, "label", optionalStringField(value, "label"))
    withOptionalString(parsed, "startRequestId", optionalStringField(value, "startRequestId"))
    withOptionalString(parsed, "startTurnId", optionalStringField(value, "startTurnId"))
    withOptionalString(parsed, "lastTurnId", optionalStringField(value, "lastTurnId"))
    withOptionalString(parsed, "lastTurnClientRequestId", optionalStringField(value, "lastTurnClientRequestId"))
    withOptionalString(parsed, "createdAt", optionalStringField(value, "createdAt"))
    withOptionalString(parsed, "updatedAt", optionalStringField(value, "updatedAt"))
    return parsed
}

function parseTaskWorktreeRecovery(value: unknown): TaskWorktreeRecovery | null {
    if (!isRecord(value)) return null
    const backupCommit = stringField(value, "backupCommit")
    const backupMessage = stringField(value, "backupMessage")
    const transferredAt = stringField(value, "transferredAt")
    const requested = value.requested
    const succeeded = value.succeeded
    const failed = value.failed
    if (!backupCommit || !backupMessage || !transferredAt || typeof requested !== "number" || typeof succeeded !== "number" || typeof failed !== "number") {
        return null
    }
    return { ...value, backupCommit, backupMessage, transferredAt, requested, succeeded, failed }
}

export function parseRepoRecord(value: unknown): RepoRecord | null {
    if (!isRecord(value) || value.v !== 1) return null
    const id = stringField(value, "id")
    const name = stringField(value, "name")
    const path = stringField(value, "path")
    const workspaceId = stringField(value, "workspaceId")
    const createdAt = stringField(value, "createdAt")
    const updatedAt = stringField(value, "updatedAt")
    if (!id || !name || !path || !workspaceId || !createdAt || !updatedAt) return null

    const parsed: RepoRecord = {
        ...copyUnknownFields(value, REPO_DERIVED_FIELDS),
        v: 1,
        id,
        name,
        path,
        workspaceId,
        createdAt,
        updatedAt,
    }
    withOptionalBoolean(parsed, "archived", optionalBooleanField(value, "archived"))
    if (isProjectColor(value.color)) parsed.color = value.color
    if (typeof value.glyph === "string") {
        const glyph = normalizeProjectGlyph(value.glyph)
        if (glyph) parsed.glyph = glyph
    }
    withOptionalString(parsed, "legacyRepoId", optionalStringField(value, "legacyRepoId"))
    if (typeof value.projectChars === "string") {
        const chars = parseProjectChars(value.projectChars)
        if (chars) parsed.projectChars = chars
    }
    if (typeof value.projectEmoji === "string") {
        const emoji = normalizeProjectEmoji(value.projectEmoji)
        if (emoji) parsed.projectEmoji = emoji
    }
    return parsed
}

export function parseTaskRecord(value: unknown): TaskRecord | null {
    if (!isRecord(value) || value.v !== 1) return null
    const id = stringField(value, "id")
    const repoId = stringField(value, "repoId")
    const workspaceId = stringField(value, "workspaceId")
    const title = stringField(value, "title")
    const createdAt = stringField(value, "createdAt")
    const updatedAt = stringField(value, "updatedAt")
    if (!id || !repoId || !workspaceId || !title || !createdAt || !updatedAt || !Array.isArray(value.sessions)) return null

    const sessions = value.sessions.map(parseTaskSessionRef)
    if (sessions.some((session) => session === null)) return null

    const historySource = optionalStringField(value, "historySource")
    if (historySource !== undefined && !TASK_HISTORY_SOURCES.has(historySource as TaskHistorySource)) return null

    const parsed: TaskRecord = {
        ...copyUnknownFields(value, TASK_DERIVED_FIELDS),
        v: 1,
        id,
        repoId,
        workspaceId,
        title,
        sessions: sessions.filter((session): session is TaskSessionRef => session !== null),
        createdAt,
        updatedAt,
    }
    withOptionalString(parsed, "activeSessionId", optionalStringField(value, "activeSessionId"))
    if (
        isRecord(value.handoff) &&
        typeof value.handoff.clientRequestId === "string" &&
        typeof value.handoff.sessionId === "string" &&
        typeof value.handoff.harnessId === "string" &&
        typeof value.handoff.model === "string"
    ) {
        parsed.handoff = {
            clientRequestId: value.handoff.clientRequestId,
            sessionId: value.handoff.sessionId,
            harnessId: value.handoff.harnessId,
            model: value.handoff.model,
            ...(isThinkingLevel(value.handoff.thinking) ? { thinking: value.handoff.thinking } : {}),
        }
    }
    withOptionalBoolean(parsed, "closed", optionalBooleanField(value, "closed"))
    withOptionalBoolean(parsed, "titleManuallySet", optionalBooleanField(value, "titleManuallySet"))
    withOptionalBoolean(parsed, "titleAgentInitialized", optionalBooleanField(value, "titleAgentInitialized"))
    withOptionalString(parsed, "globalSystemPrompt", optionalStringField(value, "globalSystemPrompt"))
    if (value.delegateTarget === null) parsed.delegateTarget = null
    else if (isRecord(value.delegateTarget)) {
        const harnessId = stringField(value.delegateTarget, "harnessId")
        const modelId = stringField(value.delegateTarget, "modelId")
        if (
            harnessId &&
            harnessId.length <= 128 &&
            modelId &&
            modelId.length <= 200 &&
            (value.delegateTarget.thinking === undefined || isThinkingLevel(value.delegateTarget.thinking))
        ) {
            parsed.delegateTarget = {
                harnessId,
                modelId,
                ...(isThinkingLevel(value.delegateTarget.thinking) ? { thinking: value.delegateTarget.thinking } : {}),
            }
        }
    }
    withOptionalString(parsed, "closedAt", optionalStringField(value, "closedAt"))
    withOptionalString(parsed, "deletingAt", optionalStringField(value, "deletingAt"))
    withOptionalBoolean(parsed, "pinned", optionalBooleanField(value, "pinned"))
    if (value.queuePriority === "major") parsed.queuePriority = "major"
    else delete parsed.queuePriority
    withOptionalString(parsed, "lastViewedAt", optionalStringField(value, "lastViewedAt"))
    withOptionalString(parsed, "legacyTaskId", optionalStringField(value, "legacyTaskId"))
    withOptionalString(parsed, "historySource", historySource)
    const enabledMcpServerIds = optionalStringArrayField(value, "enabledMcpServerIds")
    if (enabledMcpServerIds !== undefined) parsed.enabledMcpServerIds = enabledMcpServerIds
    const actionRuns = parseTaskActionRuns(value.actionRuns)
    if (actionRuns !== undefined) parsed.actionRuns = actionRuns
    const additionalDirs = optionalStringArrayField(value, "additionalDirs")
    if (additionalDirs !== undefined) parsed.additionalDirs = additionalDirs
    if (value.comments !== undefined) {
        const comments = commentsField(value.comments)
        if (!comments) return null
        parsed.comments = comments
    }
    const agentGitContext = parseTaskAgentGitContext(value.agentGitContext)
    if (agentGitContext) parsed.agentGitContext = agentGitContext
    const directorySelection = parseTaskDirectorySelection(value.directorySelection)
    if (directorySelection) parsed.directorySelection = directorySelection
    const externalLinks = parseTaskExternalLinks(value.externalLinks)
    if (externalLinks !== undefined) parsed.externalLinks = externalLinks
    else delete parsed.externalLinks
    const attention = parseTaskAttention(value.attention)
    if (attention) parsed.attention = attention
    else delete parsed.attention
    const pullRequestAutomation = parseTaskPullRequestAutomation(value.pullRequestAutomation)
    if (pullRequestAutomation !== undefined) parsed.pullRequestAutomation = pullRequestAutomation
    else delete parsed.pullRequestAutomation
    // Legacy read-only compatibility for tasks created before agent-owned worktrees.
    if (Array.isArray(value.deviceEnvironments)) parsed.deviceEnvironments = value.deviceEnvironments
    if (isRecord(value.pullRequest)) parsed.pullRequest = value.pullRequest
    if (value.worktreeRecovery !== undefined) {
        const recovery = parseTaskWorktreeRecovery(value.worktreeRecovery)
        if (!recovery) return null
        parsed.worktreeRecovery = recovery
    }
    return parsed
}

export function taskSessionIdentity(value: unknown): string | null {
    if (!isRecord(value)) return null
    const nestSessionId = stringField(value, "nestSessionId")
    if (nestSessionId) return `nest:${nestSessionId}`
    const providerId = stringField(value, "providerId")
    const providerSessionId = stringField(value, "providerSessionId")
    return providerId && providerSessionId ? `provider:${providerId}:${providerSessionId}` : null
}

function taskSessionsForWrite(sessions: TaskSessionRef[], source: TaskRecordWriteSource): Array<Record<string, unknown>> {
    return sessions.map((session) => {
        const identity = taskSessionIdentity(session)
        const sourceRole = identity ? source.sessionRoles.get(identity) : undefined
        return session.role === "auxiliary" && sourceRole ? { ...session, role: sourceRole } : session
    })
}

export function taskRecordWriteSource(value: unknown): TaskRecordWriteSource | null {
    if (!isRecord(value)) return null
    const fields: Record<string, unknown> = {}
    for (const field of TASK_RETIRED_FIELDS_PRESERVED_ON_WRITE) {
        if (value[field] !== undefined) fields[field] = value[field]
    }
    const sessionRoles = new Map<string, string>()
    if (Array.isArray(value.sessions)) {
        for (const session of value.sessions) {
            if (!isRecord(session)) continue
            const identity = taskSessionIdentity(session)
            const role = session.role
            if (identity && typeof role === "string" && role !== "primary" && role !== "auxiliary") sessionRoles.set(identity, role)
        }
    }
    return Object.keys(fields).length > 0 || sessionRoles.size > 0 ? { fields, sessionRoles } : null
}

export function taskRecordForWrite(record: TaskRecord, source: TaskRecordWriteSource | undefined): Record<string, unknown> {
    if (!source) return record
    const value: Record<string, unknown> = {
        ...record,
        sessions: taskSessionsForWrite(record.sessions, source),
    }
    for (const [field, sourceValue] of Object.entries(source.fields)) {
        if (value[field] === undefined) value[field] = sourceValue
    }
    return value
}

export function parsePersonalSettingsRecord(value: unknown): PersonalSettingsRecord | null {
    if (!isRecord(value) || value.v !== 1 || !isRecord(value.settings)) return null
    const updatedAt = stringField(value, "updatedAt")
    if (!updatedAt) return null
    return {
        ...value,
        v: 1,
        settings: value.settings as unknown as PersonalSettings,
        updatedAt,
    }
}
